/**
 * Account Login Page
 * AD-20251113-SUPABASE-AUTH-VITE: Centralized login/signup with Supabase Auth UI
 *
 * Shown when a user wants to sign in or create an account. If a session already
 * exists, this page redirects to /account without flashing or redirect loops.
 */

import React, { useEffect, useRef } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

export default function AccountLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, session } = useAuth();
  const hasRedirectedRef = useRef(false);
  // Get redirect path from query params, default to /account
  const redirectTo = searchParams.get('redirect') || '/account';

  // Handle redirect after successful login
  // This useEffect watches for when user becomes authenticated and redirects once
  useEffect(() => {
    // Only redirect if we're done loading, user is authenticated, and we haven't redirected yet
    if (!loading && user && session && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      const safeRedirect = redirectTo.startsWith('/') ? redirectTo : '/account';
      navigate(safeRedirect, { replace: true });
    }
    // Reset redirect flag if user logs out (for edge case where user logs out and back in)
    if (!loading && !user) {
      hasRedirectedRef.current = false;
    }
  }, [loading, user, session, navigate, redirectTo]);

  // While auth state is loading, show spinner
  // Per Supabase docs: wait for initial session check to complete
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If already authenticated with a valid session, show loading while redirect happens
  // The useEffect above will handle the actual redirect
  if (user && session) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Unauthenticated view: Supabase Auth UI for sign in / sign up
  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Sign in or create your Attain Domains account</CardTitle>
            <CardDescription>
              Use the same email you use at checkout so your domains, leases, and invoices stay in
              one place.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md text-sm">
              <p className="text-blue-900 dark:text-blue-100 font-medium mb-1">💡 Helpful tips</p>
              <p className="text-blue-800 dark:text-blue-200">
                New here? Enter your email to create an account — we&apos;ll send a secure link.
              </p>
              <p className="text-blue-800 dark:text-blue-200 mt-2">
                Already a customer? Sign in with the same email you used to buy or lease domains.
              </p>
            </div>

            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: '#0000FF',
                      brandAccent: '#4D7DFF',
                    },
                  },
                },
                style: {
                  button: {
                    borderRadius: '0.5rem',
                    fontFamily: 'Poppins, system-ui, sans-serif',
                  },
                  input: {
                    borderRadius: '0.5rem',
                    fontFamily: 'Poppins, system-ui, sans-serif',
                  },
                  message: {
                    borderRadius: '0.5rem',
                    fontFamily: 'Poppins, system-ui, sans-serif',
                  },
                },
              }}
              providers={[]}
              view="sign_in"
              showLinks={true}
              magicLink={true}
              // Don't use redirectTo for email/password login in SPA - let onAuthStateChange handle it
              // redirectTo is only for magic links which redirect externally
              onlyThirdPartyProviders={false}
            />
          </CardContent>
        </Card>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Secure authentication powered by Supabase. You can sign in with email and password, or use
          a magic link.
        </p>
      </div>
    </div>
  );
}


